import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

/** Kalan süre; süresiz paylaşımda null. */
export function remainingLabel(expiresAt: string | null, now: Date): string | null {
  if (!expiresAt) return null;
  const bitis = new Date(expiresAt).getTime();
  if (Number.isNaN(bitis)) return null;
  const kalanDk = Math.max(0, Math.round((bitis - now.getTime()) / 60_000));
  if (kalanDk >= 60) {
    const saat = Math.floor(kalanDk / 60);
    return `${saat}:${String(kalanDk % 60).padStart(2, '0')}`;
  }
  return `0:${String(kalanDk).padStart(2, '0')}`;
}

/**
 * Karşı taraf en son ne zaman baktı (9.3.3.6 · D-587).
 *
 * Konumunu veren kişi, verdiği şeyin ne zaman okunduğunu bilmeli: izlenmek
 * tek yönlü olursa, paylaşan karanlıkta kalır ve "kim bakıyor" sorusunun
 * cevabı yalnız izleyende olur.
 */
export function watchedLabel(lastViewedAt: string | null | undefined, now: Date): string | null {
  if (!lastViewedAt) return null;
  const an = new Date(lastViewedAt).getTime();
  if (!Number.isFinite(an)) return null;
  const sn = Math.max(0, Math.round((now.getTime() - an) / 1000));
  if (sn < 60) return `${sn}s`;
  if (sn < 3600) return `${Math.floor(sn / 60)}dk`;
  return `${Math.floor(sn / 3600)}sa`;
}

type Props = {
  expiresAt: string | null;
  lastViewedAt?: string | null;
  onStop: () => void;
  now?: Date;
};

/**
 * Paylaşım açıkken duran gösterge (9.3.3.2 · D-579).
 *
 * D-575'te paylaşımın SÜRESİZ olabilmesi kabul edildi, yani unutulan
 * paylaşımı kapatan bir saat yok. Korumanın tamamı bu çubuk: paylaşım
 * sürdüğü sürece ekranda duruyor ve durdurma tek dokunuş uzakta.
 *
 * Süreli paylaşımda kalan süre de yazıyor; süresizde yazacak bir sayı yok ve
 * uydurulmuyor.
 */
export default function LiveLocationBar({ expiresAt, lastViewedAt, onStop, now }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const an = now ?? new Date();
  const kalan = remainingLabel(expiresAt, an);
  const bakildi = watchedLabel(lastViewedAt, an);
  return (
    <View testID="live-location-bar" style={[styles.bar, { backgroundColor: colors.black }]}>
      <View style={styles.texts}>
        <Text style={[styles.text, { color: colors.white }]} numberOfLines={1}>
          {t('message.liveLocationSharing')}{kalan ? ` · ${kalan}` : ''}
        </Text>
        {/* Bakılmadıysa da yazılıyor: yokluk da bir bilgidir ve sessizlik,
            paylaşanın "belki bakılıyordur" diye varsaymasına yol açardı. */}
        <Text testID="live-location-watched" style={[styles.watched, { color: colors.white }]} numberOfLines={1}>
          {bakildi
            ? t('message.liveLocationWatchedAgo', { ago: bakildi })
            : t('message.liveLocationNotWatched')}
        </Text>
      </View>
      <TouchableOpacity
        testID="live-location-stop"
        onPress={onStop}
        accessibilityRole="button"
        // Durdurma, süresiz paylaşımın tek çıkışı: hedefi küçük bırakmak
        // korumayı zayıflatır. Simülatörde iki dokunuş boşa gitti.
        hitSlop={{ top: 12, bottom: 12, left: 16, right: 16 }}
        style={styles.stop}>
        <Text style={[styles.stopText, { color: colors.white }]}>{t('message.liveLocationStop')}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    marginHorizontal: spacing.md,
    marginBottom: spacing.xs,
  },
  texts: { flex: 1, gap: 2 },
  text: { fontSize: fontSize.sm },
  watched: { fontSize: 11, opacity: 0.7 },
  stop: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, minHeight: 36, justifyContent: 'center' },
  stopText: { fontSize: fontSize.sm, fontWeight: '700' },
});
